import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import PlacementCard from "../components/PlacementCard";

const fallbackPlacements = [
  {
    id: 1,
    company: "Finix Labs",
    role: "Fullstack Engineer",
    location: "Bengaluru | Hybrid",
    package: "10.2 LPA",
    description: "Work on customer dashboards, ship features across React and Node.",
  },
  {
    id: 2,
    company: "Northwind Data",
    role: "Data Analyst",
    location: "Remote | India",
    package: "8.5 LPA",
    description: "Build reporting pipelines, automate insights, and support stakeholder decisions.",
  },
  {
    id: 3,
    company: "Aurora Mobility",
    role: "Cloud Engineer",
    location: "Pune | On-site",
    package: "9.0 LPA",
    description: "Manage CI/CD, observability, and infra as code for mobility platforms.",
  },
  {
    id: 4,
    company: "Stellar Finance",
    role: "Frontend Developer",
    location: "Chennai | Hybrid",
    package: "9.8 LPA",
    description: "Own UI flows, accessibility, and performance for wealth products.",
  },
];

const Placements = ({ placements = [], onAddPlacement, session }) => {
  const [form, setForm] = useState({
    company: "",
    role: "",
    location: "",
    package: "",
    description: "",
  });
  const [message, setMessage] = useState("");
  const [remotePlacements, setRemotePlacements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [appliedIds, setAppliedIds] = useState(() => {
    try {
      const raw = localStorage.getItem("applied-placements");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await api.placements();
        const normalized = (data || []).map((p) => ({
          id: Number(p.id),
          company: p.company_name || p.company,
          role: p.job_role || p.role,
          location: p.location,
          package: p.package,
          description: p.description,
        }));
        setRemotePlacements(normalized);
      } catch (err) {
        setError(err.message || "Could not load placements");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const displayedPlacements =
    remotePlacements.length ? remotePlacements : placements.length ? placements : fallbackPlacements;

  const appliedSet = useMemo(() => new Set(appliedIds), [appliedIds]);

  const handleApplyClick = (placementId) => {
    if (!session) {
      navigate("/login");
      return;
    }
    if (session.role !== "Trainee") {
      setError("Only trainees can apply.");
      return;
    }
    if (appliedSet.has(placementId)) {
      setMessage("You already applied to this role.");
      return;
    }
    // Persist to backend
    api
      .applyPlacement(placementId)
      .then(() => {
        const updated = [...appliedIds, placementId];
        setAppliedIds(updated);
        localStorage.setItem("applied-placements", JSON.stringify(updated));
        setMessage("Applied successfully.");
      })
      .catch((err) => setError(err.message || "Could not apply"));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddPlacement?.({ ...form });
    setMessage("Placement added, trainees notified.");
    setForm({ company: "", role: "", location: "", package: "", description: "" });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section className="placements-page page-section">
      <header className="section-header">
        <p className="eyebrow">Placement opportunities</p>
        <h1>Open roles curated for your trainees.</h1>
        <p className="muted">
          Live openings posted by admins. No login required to browse.
        </p>
      </header>

      {loading && <p className="muted small">Loading placements...</p>}
      {error && <p className="error-callout">{error}</p>}

      <div className="placement-grid">
        {displayedPlacements.map((placement) => (
          <PlacementCard
            key={placement.id}
            placement={placement}
            onApply={handleApplyClick}
            isApplied={appliedSet.has(placement.id)}
          />
        ))}
      </div>

      <section className="placement-process">
        <div className="section-header">
          <p className="eyebrow">How we place</p>
          <h3>Clear steps that keep trainees confident.</h3>
        </div>
        <div className="timeline">
          <div className="step">
            <span className="dot positive" />
            <div>
              <h4>Role discovery</h4>
              <p>Understand requirements and map trainees based on skills and readiness.</p>
            </div>
          </div>
          <div className="step">
            <span className="dot positive" />
            <div>
              <h4>Prep and shortlist</h4>
              <p>Mock interviews, resume polish, and curated shortlists for each partner.</p>
            </div>
          </div>
          <div className="step">
            <span className="dot positive" />
            <div>
              <h4>Offer tracking</h4>
              <p>Transparent updates, onboarding checklists, and day-one readiness.</p>
            </div>
          </div>
        </div>
      </section>

      {session?.role === "Admin" && (
        <form className="placement-form form-card" onSubmit={handleSubmit}>
          <h3>Add a new placement</h3>
          <label>
            Company
            <input name="company" value={form.company} onChange={handleChange} required />
          </label>
          <label>
            Role
            <input name="role" value={form.role} onChange={handleChange} required />
          </label>
          <label>
            Location
            <input name="location" value={form.location} onChange={handleChange} required />
          </label>
          <label>
            Package
            <input name="package" value={form.package} onChange={handleChange} required />
          </label>
          <label>
            Description
            <textarea name="description" rows="3" value={form.description} onChange={handleChange} required />
          </label>
          <button type="submit" className="primary">
            Save Placement
          </button>
          {message && <p className="success-callout">{message}</p>}
        </form>
      )}
    </section>
  );
};

export default Placements;
